import { memo, useMemo } from "react"

import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { DateRange } from "../utils/date-ranges"

import { Gauge } from "@/components/icons"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { CollapsibleDashboardCard } from "./collapsible-dashboard-card"
import { useAdminUsageInsightsQuery } from "../hooks/queries/analytics"

const CHART_HEIGHT = 260
const TOOLS_SHOWN = 8

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 10_000) return `${(value / 1000).toFixed(0)}K`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return Math.round(value).toString()
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="mt-1 text-xl leading-tight font-semibold">{value}</div>
      {sub ? <div className="text-muted-foreground/80 mt-1 text-xs">{sub}</div> : null}
    </div>
  )
}

export const UsageInsightsSection = memo(function UsageInsightsSection({
  dateRange,
  open,
  onOpenChange,
}: {
  dateRange: DateRange | 7 | 30
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { data, isLoading, isFetching } = useAdminUsageInsightsQuery(dateRange, open)

  const chartData = useMemo(
    () =>
      (data?.daily ?? []).map((point) => ({
        label: point.date.slice(5), // MM-DD
        messages: point.messages,
        sessions: point.sessions,
        activeUsers: point.active_users,
      })),
    [data?.daily]
  )

  const hasData = Boolean(data && data.total_messages > 0)

  return (
    <CollapsibleDashboardCard
      icon={Gauge}
      open={open}
      title={t("adminDashboard.usageInsights.title", "Usage insights")}
      headerActions={
        isFetching && !isLoading ? (
          <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50" />
        ) : undefined
      }
      onOpenChange={onOpenChange}
    >
      {isLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-[260px] w-full rounded-xl" />
        </div>
      )}

      {!isLoading && !hasData && (
        <div className="flex h-60 items-center justify-center">
          <EmptyState
            icon={Gauge}
            title={t("adminDashboard.usageInsights.emptyTitle", "No usage data")}
            description={t(
              "adminDashboard.usageInsights.emptyDescription",
              "No assistant usage was recorded in the selected period."
            )}
          />
        </div>
      )}

      {!isLoading && hasData && data && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatTile
              label={t("adminDashboard.usageInsights.messagesPerChat", "Questions per chat")}
              value={data.avg_messages_per_session.toFixed(1)}
              sub={t("adminDashboard.usageInsights.messagesPerChatSub", {
                defaultValue: "median {{median}} · p90 {{p90}}",
                median: data.median_messages_per_session,
                p90: data.p90_messages_per_session,
              })}
            />
            <StatTile
              label={t("adminDashboard.usageInsights.callsPerMessage", "Model calls per question")}
              value={data.avg_llm_calls_per_message.toFixed(1)}
              sub={t("adminDashboard.usageInsights.callsPerMessageSub", {
                defaultValue: "{{messages}} questions in period",
                messages: data.total_messages,
              })}
            />
            <StatTile
              label={t("adminDashboard.usageInsights.tokensPerMessage", "Tokens per question")}
              value={formatCompact(data.avg_tokens_per_message)}
              sub={t("adminDashboard.usageInsights.tokensPerMessageSub", {
                defaultValue: "{{tokens}} per chat",
                tokens: formatCompact(data.avg_tokens_per_session),
              })}
            />
            <StatTile
              label={t("adminDashboard.usageInsights.activeUsers", "Active users")}
              value={String(data.active_users)}
              sub={t("adminDashboard.usageInsights.activeUsersSub", {
                defaultValue: "of {{known}} known · {{sessions}} chats each",
                known: data.known_users,
                sessions: data.avg_sessions_per_user,
              })}
            />
          </div>

          {chartData.length > 1 && (
            <ResponsiveContainer
              height={CHART_HEIGHT}
              width="100%"
              aria-label={t(
                "adminDashboard.usageInsights.trendAriaLabel",
                "Daily questions, chats and active users"
              )}
            >
              <LineChart data={chartData}>
                <CartesianGrid className="stroke-muted" strokeDasharray="3 3" />
                <XAxis
                  className="text-muted-foreground"
                  dataKey="label"
                  minTickGap={20}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  allowDecimals={false}
                  className="text-muted-foreground"
                  domain={[0, "auto"]}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  itemStyle={{ color: "hsl(var(--popover-foreground))" }}
                  labelStyle={{ color: "hsl(var(--popover-foreground))" }}
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "0.5rem",
                    color: "hsl(var(--popover-foreground))",
                  }}
                />
                <Legend />
                <Line
                  dataKey="messages"
                  dot={false}
                  name={t("adminDashboard.usageInsights.seriesMessages", "Questions")}
                  stroke="hsl(var(--chart-1))"
                  strokeWidth={2}
                  type="monotone"
                />
                <Line
                  dataKey="sessions"
                  dot={false}
                  name={t("adminDashboard.usageInsights.seriesSessions", "Chats")}
                  stroke="hsl(var(--chart-2))"
                  strokeWidth={2}
                  type="monotone"
                />
                <Line
                  dataKey="activeUsers"
                  dot={false}
                  name={t("adminDashboard.usageInsights.seriesActiveUsers", "Active users")}
                  stroke="hsl(var(--chart-3))"
                  strokeWidth={2}
                  type="monotone"
                />
              </LineChart>
            </ResponsiveContainer>
          )}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div>
              <h4 className="mb-2 text-sm font-medium">
                {t("adminDashboard.usageInsights.byModelTitle", "Cost shape per model")}
              </h4>
              <div className="space-y-2 lg:hidden">
                {data.by_model.map((row) => (
                  <div key={`${row.model}-mobile`} className="rounded-lg border p-3 text-sm">
                    <div className="truncate font-medium">{row.model}</div>
                    <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground block">Questions</span>
                        <span className="font-semibold tabular-nums">{row.messages}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Calls/question</span>
                        <span className="font-semibold tabular-nums">
                          {row.calls_per_message.toFixed(1)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Tokens/question</span>
                        <span className="font-semibold tabular-nums">
                          {formatCompact(row.tokens_per_message)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Tokens/chat</span>
                        <span className="font-semibold tabular-nums">
                          {formatCompact(row.tokens_per_session)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("adminDashboard.usageInsights.colModel", "Model")}</TableHead>
                      <TableHead className="text-right">
                        {t("adminDashboard.usageInsights.colMessages", "Questions")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("adminDashboard.usageInsights.colCallsPerMessage", "Calls/question")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("adminDashboard.usageInsights.colTokensPerMessage", "Tokens/question")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("adminDashboard.usageInsights.colTokensPerSession", "Tokens/chat")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_model.map((row) => (
                      <TableRow key={row.model}>
                        <TableCell className="font-medium">{row.model}</TableCell>
                        <TableCell className="text-right">{row.messages}</TableCell>
                        <TableCell className="text-right">
                          {row.calls_per_message.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCompact(row.tokens_per_message)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCompact(row.tokens_per_session)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-medium">
                {t("adminDashboard.usageInsights.toolsTitle", "Tool usage")}
              </h4>
              {data.tools.length === 0 ? (
                <div className="text-muted-foreground py-6 text-sm">
                  {t("adminDashboard.usageInsights.toolsEmpty", "No tool calls in this period.")}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("adminDashboard.usageInsights.colTool", "Tool")}</TableHead>
                      <TableHead className="text-right">
                        {t("adminDashboard.usageInsights.colCalls", "Calls")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("adminDashboard.usageInsights.colMessagesWithTool", "Questions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tools.slice(0, TOOLS_SHOWN).map((row) => (
                      <TableRow key={row.tool}>
                        <TableCell className="font-medium">{row.tool}</TableCell>
                        <TableCell className="text-right">{row.calls}</TableCell>
                        <TableCell className="text-right">{row.messages}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatTile
              label={t("adminDashboard.usageInsights.topUsersShare", "Top 10 users' token share")}
              value={`${data.top_users_token_share}%`}
            />
            <StatTile
              label={t("adminDashboard.usageInsights.topChatsShare", "Top 10 chats' token share")}
              value={`${data.top_chats_token_share}%`}
            />
          </div>

          <p className="text-muted-foreground/80 text-xs">
            {t(
              "adminDashboard.usageInsights.scopeNote",
              "Covers assistant usage only — background helpers such as chat title generation are not included."
            )}
          </p>
        </div>
      )}
    </CollapsibleDashboardCard>
  )
})
